import React, { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import BrandLoader from '../../../shared/brand/BrandLoader';
import { FIELD_LABEL_CLASS, FIELD_HELP_CLASS, FIELD_GAP } from '../../../shared/TailwindComponents/FieldShell';
import { useTranslation } from 'react-i18next';
import {
  PiArrowLeftLight,
  PiDownloadSimpleLight,
  PiClockLight,
  PiSpinnerGapLight,
  PiCheckCircleLight,
  PiArrowsClockwiseLight,
  PiCaretLeftLight,
  PiArrowsOutSimpleLight,
  PiArrowsInSimpleLight,
  PiCloudArrowDownLight,
  PiFolderPlus,
  PiArrowCounterClockwiseLight,
  PiClockCounterClockwiseLight,
  PiLightbulbLight,
  PiSlidersHorizontalLight,
  PiSparkleLight,
  PiDotsThreeVerticalLight,
} from 'react-icons/pi';
import { Menu, Transition } from '@headlessui/react';
import apis from '../../../../utils/apis';
import { useAppDispatch } from '../../../../store';
import { showFeedbackModal } from '../../../../store/actions/UserFeedbackActions';
import { hideModal, showModal } from '../../../../store/actions/modalActions';
import { finalizeInsight, unfinalizeInsight } from '../../../../store/actions/Insights/InsightsActions';
import { fetchBrandingTemplates } from '../../../../store/actions/TemplateActions';
import { isTemplatesEnabled } from '../../../../utils/config';
import { showDocumentCustomizationPanel } from '../../../../store/actions/sidePanelActions';
import Badge from '../../../shared/TailwindComponents/Badge';
import Button from '../../../shared/TailwindComponents/Button';
import Radio from '../../../shared/TailwindComponents/Radio';
import Dropdown from '../../../shared/TailwindComponents/Dropdown';
import { formatDateTime } from '../../../../utils/functions';
import { extractHtmlFromDocx, base64ToBlob } from '../../../../utils/docxHandler';
import { LoadingToCheckmark } from '../../../shared/animations/LoadingToCheckmark';
import { useFeatureTour } from '../../../../hooks/useFeatureTour';
import { FeatureTour, insightViewerTourSteps, insightViewerNotGeneratedTourSteps } from '../../../shared/FeatureTour';
import Spinner from '../../../shared/TailwindComponents/Spinner';
import ModalShell from '../../../shared/ModalShell';

interface InsightVersion {
  version_id: string;
  modified: string;
  size_bytes: number;
  name: string;
  status: string;
  is_current?: boolean;
}

interface InsightMetadata {
  insight_type_id: string;
  name: string;
  status: string;
  generated_at: string;
  word_count: number;
  version: number;
  error_message?: string | null;
  finalized?: boolean;
  finalized_at?: string;
}

interface InsightViewerProps {
  matterUuid: string;
  insightTypeId: string;
  insightName: string;
  onBack: () => void;
  onRerun: (insightTypeId: string) => void;
  onGenerate?: (insightTypeId: string) => void;
  generatingInsightId: string | null;
  initialVersionId?: string;
  onFinalized?: () => void;
  processingMessage?: string;
  processingProgress?: number;
}

/**
 * Extract body content and styles from a full HTML document
 * Returns { bodyHtml, styleSheet }
 */
function extractHtmlParts(fullHtml: string): { bodyHtml: string; styleSheet?: string } {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(fullHtml, 'text/html');

    // Extract styles from head
    const styleElement = doc.querySelector('head style');
    const styleSheet = styleElement?.textContent || '';

    // Extract body content
    const bodyElement = doc.querySelector('body');
    const bodyHtml = bodyElement?.innerHTML || fullHtml;

    return { bodyHtml, styleSheet };
  } catch (error) {
    console.error('Error parsing HTML document:', error);
    // If parsing fails, return the original HTML
    return { bodyHtml: fullHtml };
  }
}

/**
 * Wrap HTML content in a complete document with styles for iframe rendering
 */
function wrapHtmlForIframe(html: string): string {
  try {
    const { bodyHtml, styleSheet } = extractHtmlParts(html);

    // Custom scrollbar styles for iframe
    const customScrollbarStyles = `
      html {
        scrollbar-width: thin;
        scrollbar-color: rgba(5, 9, 21, 0.7) transparent;
      }

      html::-webkit-scrollbar {
        width: 8px;
      }

      html::-webkit-scrollbar-track {
        background: transparent;
      }

      html::-webkit-scrollbar-thumb {
        background: rgba(5, 9, 21, 0.7);
        border-radius: 4px;
      }

      html::-webkit-scrollbar-thumb:hover {
        background: rgba(5, 9, 21, 0.95);
      }
    `;

    // Reconstruct the full HTML document for iframe
    if (styleSheet && styleSheet.trim().length > 0) {
      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    ${styleSheet}
    ${customScrollbarStyles}
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
    } else {
      // Fallback: wrap body HTML in minimal HTML structure with custom scrollbar
      return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    ${customScrollbarStyles}
  </style>
</head>
<body>
  ${bodyHtml}
</body>
</html>`;
    }
  } catch (error) {
    console.error('Error wrapping HTML:', error);
    return html;
  }
}

const InsightViewer: React.FC<InsightViewerProps> = ({
  matterUuid,
  insightTypeId,
  insightName,
  onBack,
  onRerun,
  onGenerate,
  generatingInsightId,
  initialVersionId,
  onFinalized,
  processingMessage,
  processingProgress
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [docxHtml, setDocxHtml] = useState<string>('');
  const [insightMetadata, setInsightMetadata] = useState<InsightMetadata | null>(null);
  const [versions, setVersions] = useState<InsightVersion[]>([]);
  const [currentVersionId, setCurrentVersionId] = useState<string>('');
  const [currentVersion, setCurrentVersion] = useState<InsightVersion | null>(null);
  const [blobMetadataVersionId, setBlobMetadataVersionId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [error, setError] = useState<string>('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [notGenerated, setNotGenerated] = useState(false);
  const [refreshAfterGeneration, setRefreshAfterGeneration] = useState(false);

  // Download modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<'docx' | 'pdf'>('docx');
  const [downloadBrandingTemplateId, setDownloadBrandingTemplateId] = useState<string>('');
  const [brandingTemplates, setBrandingTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const isGenerating = generatingInsightId === insightTypeId;
  const prevIsGeneratingRef = useRef(isGenerating);

  // Tour: choose steps based on whether the insight has been generated
  const tourSteps = notGenerated ? insightViewerNotGeneratedTourSteps : insightViewerTourSteps;
  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: notGenerated ? 'insight-viewer-not-generated' : 'insight-viewer',
    steps: tourSteps,
    readyToShow: !loading && !isGenerating,
    autoStartDelay: 600,
  });

  useEffect(() => {
    loadInsightData(initialVersionId);
  }, [matterUuid, insightTypeId, initialVersionId]);

  // Detect when generation completes (isGenerating transitions true → false)
  // The parent component manages socket events and clears the generating state.
  // When that happens, we reload the document content and re-stream it.
  useEffect(() => {
    const wasGenerating = prevIsGeneratingRef.current;
    prevIsGeneratingRef.current = isGenerating;

    if (wasGenerating && !isGenerating) {
      // Generation just completed - reload the document after a brief delay
      // to give blob storage time to be fully consistent.
      // Use refreshAfterGeneration flag so we keep showing the generating/checkmark UI
      // instead of flashing a loading skeleton.
      setNotGenerated(false);
      setRefreshAfterGeneration(true);
      setTimeout(() => {
        loadInsightData();
      }, 1500);
    }
  }, [isGenerating]);

  const loadInsightData = async (versionId?: string) => {
    try {
      // Only show loading skeleton on initial load, not on post-generation refresh
      if (!refreshAfterGeneration) {
        setLoading(true);
      }
      setError('');

      const requestBody = versionId ? { version_id: versionId } : {};
      const response = await apis.post(`/insights/${matterUuid}/${insightTypeId}`, requestBody);

      if (response.status === 200) {
        // Handle not_generated response (200 with status='not_generated')
        if (response.data?.status === 'not_generated') {
          if (isGenerating) {
            setNotGenerated(false);
            setError('');
          } else {
            setNotGenerated(true);
            setError('');
          }
          setLoading(false);
          setRefreshAfterGeneration(false);
          return;
        }

        const { blob_metadata, document_metadata, versions: responseVersions } = response.data;

        setInsightMetadata(document_metadata || null);
        // Check blob_metadata for "final" key (set on blob as metadata)
        setIsFinalized((blob_metadata as any)?.final === 'true' || false);

        const versionsToUse = responseVersions || [];
        setVersions(versionsToUse);

        // Set the blob_metadata version_id as the current version marker
        if (blob_metadata?.version_id) {
          setBlobMetadataVersionId(blob_metadata.version_id);
        }

        // If viewing a specific version, set that as currentVersionId
        // Otherwise, use the blob_metadata version (the latest)
        const viewingVersionId = versionId || blob_metadata?.version_id;

        if (viewingVersionId) {
          setCurrentVersionId(viewingVersionId);

          const currentVersionData = versionsToUse.find(
            (v: InsightVersion) => v.version_id === viewingVersionId
          );
          if (currentVersionData) {
            setCurrentVersion(currentVersionData);
          }
        } else if (versionsToUse.length > 0) {
          setCurrentVersionId(versionsToUse[0].version_id);
          setCurrentVersion(versionsToUse[0]);
          if (!blob_metadata?.version_id) {
            setBlobMetadataVersionId(versionsToUse[0].version_id);
          }
        }

        await loadDocxStream(versionId);
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404 || error?.response?.data?.status === 'not_generated') {
        // If actively generating, don't show "not generated" - let the generating UI show
        if (isGenerating) {
          setNotGenerated(false);
          setError('');
        } else {
          // This insight hasn't been generated yet - show placeholder
          setNotGenerated(true);
          setError('');
        }
      } else {
        console.error('Failed to load insight:', error);
        setError('Failed to load insight content');
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Failed to load insight content'),
            modalType: 'error',
            duration: 5000
          })
        );
      }
    } finally {
      setLoading(false);
      setRefreshAfterGeneration(false);
    }
  };

  const loadDocxStream = async (versionId?: string) => {
    try {
      const requestBody = versionId ? { version_id: versionId } : {};
      const response = await apis.post(
        `/insights/${matterUuid}/${insightTypeId}/stream`,
        requestBody,
        { responseType: 'blob' }
      );

      if (response.status !== 200) {
        throw new Error(`Failed to stream DOCX: ${response.status}`);
      }

      let html: string;
      let blob = response.data as Blob;

      // Check if response is a JSON not_generated response
      if (blob.type === 'application/json') {
        const jsonText = await blob.text();
        try {
          const jsonData = JSON.parse(jsonText);
          if (jsonData.status === 'not_generated') {
            // Document not generated yet - handled by the caller
            return false;
          }
        } catch {
          // Not valid JSON, continue with blob processing
        }
      }

      // Check if response is Base64-encoded (text) or binary (blob)
      if (blob.type === 'text/plain') {
        // Handle Base64 response
        const base64String = await blob.text();
        blob = base64ToBlob(base64String.trim());
      }

      const arrayBuffer = await blob.arrayBuffer();
      html = await extractHtmlFromDocx(arrayBuffer);

      if (!html || html.trim().length === 0) {
        throw new Error('Failed to extract HTML from DOCX');
      }

      // Wrap HTML in a complete document for proper iframe rendering
      const wrappedHtml = wrapHtmlForIframe(html);
      setDocxHtml(wrappedHtml);
      return true;
    } catch (error) {
      console.error('Failed to stream DOCX:', error);
      setError('Failed to load document');
      return false;
    }
  };

  const handleOpenDownloadModal = async () => {
    setShowDownloadModal(true);
    setDownloadFormat('docx');
    setDownloadBrandingTemplateId('');

    // Only fetch branding templates on localhost/dev
    if (!isTemplatesEnabled()) return;
    setLoadingTemplates(true);

    try {
      const result = await dispatch(fetchBrandingTemplates({}) as any);
      setBrandingTemplates(result?.templates || []);
    } catch (err) {
      console.error('Failed to fetch branding templates:', err);
      setBrandingTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleConfirmDownload = async () => {
    setShowDownloadModal(false);
    try {
      setDownloading(true);

      const mimeType = downloadFormat === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const extension = downloadFormat === 'pdf' ? '.pdf' : '.docx';

      const response = await apis.post(
        `/insights/${matterUuid}/${insightTypeId}/stream`,
        {
          version_id: currentVersionId,
          disposition: 'attachment',
          format: downloadFormat,
          branding_template_id: downloadBrandingTemplateId || null
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${insightName.replace(/\s+/g, '-')}${extension}`;
      a.click();
      URL.revokeObjectURL(url);

      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Insight downloaded successfully'),
          modalType: 'success',
          duration: 3000
        })
      );
    } catch (error) {
      console.error('Failed to download insight:', error);
      dispatch(
        showFeedbackModal({
          showModal: true,
          message: t('Failed to download insight'),
          modalType: 'error',
          duration: 5000
        })
      );
    } finally {
      setDownloading(false);
      setDownloadBrandingTemplateId('');
    }
  };

  const verifyVersions = useCallback(async () => {
    try {
      const res = await apis.get(`/insights/${matterUuid}/${insightTypeId}/verify-versions`);
      console.log('Verify Versions Response:', res.data);
      return res.data;
    } catch (error) {
      console.error('Verify versions failed:', error);
    }
  }, [matterUuid, insightTypeId]);

  const compareVersions = useCallback(async (version1: string, version2: string) => {
    try {
      const res = await apis.get(`/insights/${matterUuid}/${insightTypeId}/compare-versions`, {
        params: { version1, version2 }
      });
      console.log('Compare Versions Response:', res.data);
      return res.data;
    } catch (error) {
      console.error('Compare versions failed:', error);
    }
  }, [matterUuid, insightTypeId]);

  const loadVersion = useCallback(
    async (versionId: string) => {
      try {
        setLoading(true);
        const response = await apis.post(`/insights/${matterUuid}/${insightTypeId}`, {
          version_id: versionId
        });

        if (response.status === 200) {
          const { document_metadata } = response.data;
          setInsightMetadata(document_metadata || null);
          setCurrentVersionId(versionId);

          const versionData = versions.find(v => v.version_id === versionId);
          if (versionData) {
            setCurrentVersion(versionData);
          }

          await loadDocxStream(versionId);

          if (versions.length > 1) {
            verifyVersions();
          }
        }
      } catch (error) {
        console.error('Failed to load version:', error);
        dispatch(
          showFeedbackModal({
            showModal: true,
            message: t('Failed to load version'),
            modalType: 'error',
            duration: 5000
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [matterUuid, insightTypeId, versions, dispatch, t, verifyVersions]
  );

  const handleRestore = useCallback(async (versionId: string) => {
    try {
      setRestoring(true);
      await apis.post(`/insights/${matterUuid}/${insightTypeId}/restore`, {
        version_id: versionId
      });

      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Insight restored successfully'),
        subMsg: `Version restored and set as current`,
        modalType: 'success',
        duration: 3000
      }));

      setTimeout(() => {
        loadInsightData();
      }, 1000);
    } catch (error) {
      console.error('Failed to restore insight version:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to restore insight version'),
        modalType: 'error',
        duration: 5000
      }));
    } finally {
      setRestoring(false);
    }
  }, [matterUuid, insightTypeId, dispatch, t]);

  const handleFinalizeInsight = () => {
    dispatch(
      showModal({
        type: 'confirmation',
        message: t(`Finalize ${insightName}?`),
        subMsg: t('This will mark the insight as complete and store it in your Files folder under this matter. You can still restore it later if needed.'),
        icon: <PiCheckCircleLight className='w-6 h-6 text-success-600' />,
        onConfirm: async () => {
          setFinalizing(true);
          try {
            const success = await dispatch(finalizeInsight(matterUuid, insightTypeId) as any);
            if (success) {
              setIsFinalized(true);
              if (onFinalized) {
                onFinalized();
              }
            }
          } finally {
            setFinalizing(false);
            dispatch(hideModal())
          }
        },
        showModal: true
      })
    );
  };

  const handleUnfinalizeInsight = () => {
    dispatch(
      showModal({
        type: 'confirmation',
        message: t(`Restore ${insightName}?`),
        subMsg: t('This will remove the finalized status from the insight. The copy in your Files folder will remain, but the insight can be edited again.'),
        onConfirm: async () => {
          setFinalizing(true);
          try {
            const success = await dispatch(unfinalizeInsight(matterUuid, insightTypeId) as any);
            if (success) {
              setIsFinalized(false);
              if (onFinalized) {
                onFinalized();
              }
            }
          } finally {
            setFinalizing(false);
            dispatch(hideModal())
          }
        },
        showModal: true
      })
    );
  };

  return (
    <div className={`flex flex-col bg-surface overflow-hidden scroll-smooth ${isExpanded ? 'fixed inset-0 z-50' : 'h-full min-h-0'}`}>
      <div className={`border-b border-hairline px-4 py-2 ${isExpanded ? 'bg-surface-sunken' : ''}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <button
              onClick={isExpanded ? () => setIsExpanded(false) : onBack}
              className="p-1.5 hover:bg-surface-sunken rounded transition-colors flex-shrink-0"
              title={isExpanded ? t('Minimize') : t('Back')}
            >
              <PiCaretLeftLight className="h-4 w-4 rtl:-scale-x-100" />
            </button>
            <h1 className="text-sm font-medium truncate text-gray-900 dark:text-white">{insightName}</h1>
          </div>

          <div data-tour-id="insight-viewer-toolbar" className="flex items-center gap-2 flex-shrink-0">
            {insightMetadata && currentVersion && (
              <>
                <Badge color='gray'>
                  <span className="text-xs">{insightMetadata.word_count?.toLocaleString() || '0'} words</span>
                </Badge>
                <Badge color='gray'>
                  <span className="text-xs">{formatDateTime(currentVersion.modified)}</span>
                </Badge>
                {isFinalized && (
                  <Badge
                    color='green'
                    tooltip={insightMetadata?.finalized_at ? `Finalized on ${formatDateTime(insightMetadata.finalized_at)}` : 'Finalized'}
                  >
                    <span className="text-xs flex items-center gap-1">
                      <PiCheckCircleLight className="h-3 w-3" />
                      {t('Finalized')}
                    </span>
                  </Badge>
                )}
              </>
            )}

            {!notGenerated && <div className="h-6 w-px bg-hairline"></div>}

            {currentVersionId !== blobMetadataVersionId && !isFinalized && !notGenerated && (
              <Button
                variant="outline"
                size="small"
                onClick={() => handleRestore(currentVersionId)}
                disabled={restoring}
                icon={restoring ? <Spinner size="glyph" className="!h-3.5 !w-3.5" /> : <PiArrowCounterClockwiseLight className="h-3.5 w-3.5" />}
              >
                {restoring ? t('Restoring...') : t('Restore')}
              </Button>
            )}

            {!isFinalized && !notGenerated && (
              <div data-tour-id="insight-viewer-rerun">
              <Button
                variant="primary"
                size="small"
                onClick={() => onRerun(insightTypeId)}
                disabled={isGenerating}
                icon={isGenerating ? <BrandLoader size="xs" className="!h-3.5 !w-3.5 ms-1" /> : <PiArrowsClockwiseLight className="ms-1 h-3.5 w-3.5" />}
                title={t('Regenerate the insight')}
              >
                {isGenerating ? t('Rerunning...') : t('Rerun')}
              </Button>
              </div>
            )}

            {!notGenerated && (
              <div data-tour-id="insight-viewer-versions">
              <Button
                variant="outline"
                size="small"
                onClick={() => setShowVersions(!showVersions)}
                disabled={versions.length <= 1}
              >
                {versions.length > 1 ? `${versions.length}` : '-'}
              </Button>
              </div>
            )}

            {/* Three-dot menu for Customize, Complete/Restore, and Download */}
            {!notGenerated && (
              <Menu as="div" data-tour-id="insight-viewer-menu" className="relative inline-block text-start">
                <Menu.Button as={Button} variant='outline' size='small' disabled={loading} title={t('More actions')}>
                  <PiDotsThreeVerticalLight className="h-4 text-gray-600 dark:text-gray-300" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-100"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute end-0 mt-2 w-48 origin-top-right rounded-md bg-surface shadow-lg ring-1 ring-black/5 dark:ring-white/10 focus:outline-none z-50">
                    <div className="py-1">
                      {insightMetadata?.status === 'completed' && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => dispatch(showDocumentCustomizationPanel(matterUuid, insightTypeId, 'insight', insightName))}
                              className={`${active ? 'bg-surface-sunken' : ''} flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200`}
                            >
                              <PiSlidersHorizontalLight className="h-4 w-4" />
                              {t('Customize')}
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      {insightMetadata?.status === 'completed' && !isFinalized && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleFinalizeInsight}
                              disabled={finalizing}
                              className={`${active ? 'bg-surface-sunken' : ''} flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50`}
                            >
                              {finalizing ? <Spinner size="glyph" className="!h-4 !w-4" /> : <PiFolderPlus className="h-4 w-4" />}
                              {finalizing ? t('Finalizing...') : t('Finalize')}
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      {insightMetadata?.status === 'completed' && isFinalized && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={handleUnfinalizeInsight}
                              disabled={finalizing}
                              className={`${active ? 'bg-surface-sunken' : ''} flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50`}
                            >
                              {finalizing ? <Spinner size="glyph" className="!h-4 !w-4" /> : <PiArrowCounterClockwiseLight className="h-4 w-4" />}
                              {finalizing ? t('Restoring...') : t('Restore finalized')}
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleOpenDownloadModal}
                            disabled={downloading || !docxHtml}
                            className={`${active ? 'bg-surface-sunken' : ''} flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50`}
                          >
                            {downloading ? <Spinner size="glyph" className="!h-4 !w-4" /> : <PiCloudArrowDownLight className="h-4 w-4" />}
                            {downloading ? t('Downloading...') : t('Download')}
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 hover:bg-surface-sunken rounded transition-colors flex-shrink-0"
              title={isExpanded ? t('Minimize') : t('Expand')}
            >
              {isExpanded ? <PiArrowsInSimpleLight className="ms-1 h-4 w-4" /> : <PiArrowsOutSimpleLight className="ms-1 h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className={`flex-1 flex gap-4 overflow-hidden px-4 py-4 ${isExpanded ? 'px-0 py-0' : ''}`}>
        <div className={`flex-1 flex flex-col min-w-0 overflow-hidden ${isExpanded ? 'h-full' : ''}`}>
          {loading ? (
            <div className="flex-1 flex justify-center items-start py-4">
              <div className="w-full space-y-4">
                <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
                <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
              </div>
            </div>
          ) : notGenerated && !isGenerating ? (
            <div data-tour-id="insight-viewer-not-generated" className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="mb-6">
                  <div className="mx-auto flex items-center justify-center mb-4">
                    <PiLightbulbLight className="h-10 w-10 text-gray-400 dark:text-gray-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {t('Insight not generated yet')}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('This insight has not been generated for this matter. Generate it to get AI-powered analysis, or customize the parameters first.')}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => onGenerate ? onGenerate(insightTypeId) : onRerun(insightTypeId)}
                    icon={<PiSparkleLight className="h-4 w-4" />}
                  >
                    {t('Generate')}
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => dispatch(showDocumentCustomizationPanel(matterUuid, insightTypeId, 'insight', insightName))}
                    icon={<PiSlidersHorizontalLight className="h-4 w-4" />}
                  >
                    {t('Customize')}
                  </Button>
                </div>
              </div>
            </div>
          ) : isGenerating ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-sm">
                <LoadingToCheckmark isLoading={isGenerating} serviceName="Insight generation" />
                {processingMessage && (
                  <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 animate-fade-in">
                    {processingMessage}
                  </p>
                )}
                {processingProgress !== undefined && processingProgress > 0 && processingProgress < 100 && (
                  <div className="mt-3 w-full max-w-xs mx-auto">
                    <div className="h-1.5 bg-hairline rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${processingProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{Math.round(processingProgress)}%</p>
                  </div>
                )}
              </div>
            </div>
          ) : refreshAfterGeneration ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-sm">
                <LoadingToCheckmark isLoading={false} serviceName="Insight generation" />
                <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 animate-fade-in">
                  Loading your document...
                </p>
              </div>
            </div>
          ) : error ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-danger-600 dark:text-danger-400 mb-2">{error}</p>
                <Button onClick={() => loadInsightData()} variant="primary" size="small">
                  Try Again
                </Button>
              </div>
            </div>
          ) : docxHtml ? (
            <div className="flex-1 flex justify-center items-start py-4">
              <iframe
                title={t('Document viewer')}
                srcDoc={docxHtml}
                style={{
                  width: '860px',
                  height: isExpanded ? '88vh' : '75vh',
                  minHeight: isExpanded ? '88vh' : '75vh',
                  border: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'block'
                }}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-400">{t('No document available')}</p>
            </div>
          )}
        </div>

        {showVersions && versions.length > 0 && !isExpanded && (
          <div className="w-64 flex-shrink-0 bg-surface-sunken rounded-lg border border-hairline p-3 overflow-y-auto custom-scrollbar-main flex flex-col">
            <h3 className="text-xs font-medium text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <PiClockCounterClockwiseLight className="h-3.5 w-3.5" />
              Versions
            </h3>

            <div className="space-y-2 flex-1">
              {versions.map((version) => {
                const isActualCurrent = blobMetadataVersionId === version.version_id;
                const isViewing = currentVersionId === version.version_id;
                const canSelectVersion = !isFinalized || isActualCurrent;
                return (
                  <button
                    key={version.version_id}
                    onClick={() => loadVersion(version.version_id)}
                    disabled={loading || restoring || !canSelectVersion}
                    title={!canSelectVersion ? t('Cannot restore older versions while finalized') : undefined}
                    className={`w-full text-start p-2.5 rounded text-xs border transition-all ${
                      isViewing
                        ? 'bg-info-50 dark:bg-info-900/30 border-info-300 dark:border-info-600'
                        : isActualCurrent
                        ? 'bg-success-50 dark:bg-success-900/30 border-success-300 dark:border-success-600'
                        : 'bg-surface border-hairline hover:border-fg-muted/40'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                          {formatDateTime(version.modified)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isViewing && <PiCheckCircleLight className="h-3.5 w-3.5 text-info-600 dark:text-info-400" />}
                        {isActualCurrent && <span className="text-xs px-1.5 py-0.5 bg-success-100 dark:bg-success-900/50 text-success-700 dark:text-success-300 rounded">{isFinalized ? t('Finalized') : t('Current')}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Download Insight Modal */}
      <ModalShell
        isOpen={showDownloadModal}
        onClose={() => {
          setShowDownloadModal(false);
          setDownloadBrandingTemplateId('');
        }}
        title={t('Download insight')}
        size="sm"
        footer={
          <>
            <Button
              variant="neutral"
              size="small"
              onClick={() => {
                setShowDownloadModal(false);
                setDownloadBrandingTemplateId('');
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={handleConfirmDownload}
              icon={<PiCloudArrowDownLight className="h-4 w-4" />}
            >
              {t('Download')}
            </Button>
          </>
        }
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('Output format')}
          </label>
          <div className="flex gap-4">
            <Radio
              name="downloadFormat"
              value="docx"
              label={t('Word (.docx)')}
              checked={downloadFormat === 'docx'}
              onChange={() => setDownloadFormat('docx')}
            />
            <Radio
              name="downloadFormat"
              value="pdf"
              label={t('PDF (.pdf)')}
              checked={downloadFormat === 'pdf'}
              onChange={() => setDownloadFormat('pdf')}
            />
          </div>
        </div>

        {isTemplatesEnabled() && (
        <div className="mt-4">
          <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>{t('Apply branding template')}</p>
          <p className={`${FIELD_HELP_CLASS} ${FIELD_GAP}`}>{t('Optional')}</p>
          {loadingTemplates ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Spinner size="glyph" className="!h-4 !w-4" />
              {t('Loading templates...')}
            </div>
          ) : (
            <Dropdown
              value={downloadBrandingTemplateId}
              onChange={(value) => setDownloadBrandingTemplateId(value as string)}
              options={[
                { value: '', label: t('No branding (plain document)') },
                ...brandingTemplates.map(template => ({
                  value: template.id,
                  label: template.TemplateName
                }))
              ]}
              placeholder={t('Select a branding template')}
              fullWidth
            />
          )}
          {brandingTemplates.length === 0 && !loadingTemplates && (
            <p className="text-xs text-gray-400 mt-1">
              {t('No branding templates available. Create one in Templates → Document Branding.')}
            </p>
          )}
        </div>
        )}
      </ModalShell>
      <FeatureTour
        steps={tourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default InsightViewer;
